import React from 'react';
import { Palette, Check } from 'lucide-react';
import { getAvailableThemes } from '../../config/themes';

const ThemeSelector = ({ currentTheme, onThemeChange, isAdmin }) => {
  const availableThemes = getAvailableThemes();

  if (!isAdmin) {
    return null; // Only show to admins for now
  }

  return (
    <div className="bg-gray-800 border-2 border-purple-600 rounded-lg p-6">
      <div className="flex items-center gap-3 mb-4">
        <Palette className="text-purple-400" size={24} />
        <h3 className="text-xl font-bold text-purple-300">App Theme</h3>
      </div>
      
      <p className="text-gray-300 mb-4">
        Choose the animated background theme for the application.
        {isAdmin && (
          <span className="block mt-1 text-purple-300 text-sm font-medium">
            🌐 Admin: Theme changes apply globally for all users
          </span>
        )}
      </p>

      <div className="grid gap-3">
        {availableThemes.map(theme => (
          <button
            key={theme.id}
            onClick={() => onThemeChange(theme)}
            className={`p-4 rounded-lg border-2 transition-all text-left ${
              currentTheme.id === theme.id
                ? 'border-purple-500 bg-purple-900/30'
                : 'border-gray-600 bg-gray-700 hover:border-purple-400 hover:bg-gray-600'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h4 className={`font-semibold ${
                    currentTheme.id === theme.id ? 'text-purple-200' : 'text-gray-200'
                  }`}>
                    {theme.name}
                  </h4>
                  {currentTheme.id === theme.id && (
                    <Check size={16} className="text-purple-400" />
                  )}
                </div>
                <p className={`text-sm ${
                  currentTheme.id === theme.id ? 'text-purple-300' : 'text-gray-400'
                }`}>
                  {theme.description}
                </p>
                
                {/* Theme Details */}
                <div className="mt-2 flex flex-wrap gap-2">
                  {theme.creatures?.bats?.enabled && (
                    <span
                      className={`px-2 py-1 rounded text-xs ${
                        currentTheme.id === theme.id
                          ? 'bg-purple-800 text-purple-200'
                          : 'bg-gray-600 text-gray-300'
                      }`}
                    >
                      🦇 Bats
                    </span>
                  )}
                  {theme.creatures?.mothman?.enabled && (
                    <span
                      className={`px-2 py-1 rounded text-xs ${
                        currentTheme.id === theme.id
                          ? 'bg-purple-800 text-purple-200'
                          : 'bg-gray-600 text-gray-300'
                      }`}
                    >
                      👹 Mothman
                    </span>
                  )}
                  {theme.creatures?.eyes?.enabled && (
                    <span
                      className={`px-2 py-1 rounded text-xs ${
                        currentTheme.id === theme.id
                          ? 'bg-purple-800 text-purple-200'
                          : 'bg-gray-600 text-gray-300'
                      }`}
                    >
                      👁️ Glowing Eyes
                    </span>
                  )}
                  {theme.creatures?.sandParticles?.enabled && (
                    <span
                      className={`px-2 py-1 rounded text-xs ${
                        currentTheme.id === theme.id
                          ? 'bg-purple-800 text-purple-200'
                          : 'bg-gray-600 text-gray-300'
                      }`}
                    >
                      🌪️ Sand Particles
                    </span>
                  )}
                  {(!theme.creatures?.bats?.enabled && !theme.creatures?.mothman?.enabled && !theme.creatures?.eyes?.enabled && !theme.creatures?.sandParticles?.enabled) && (
                    <span
                      className={`px-2 py-1 rounded text-xs ${
                        currentTheme.id === theme.id
                          ? 'bg-purple-800 text-purple-200'
                          : 'bg-gray-600 text-gray-300'
                      }`}
                    >
                      🌫️ Minimal
                    </span>
                  )}
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Preview Note */}
      <div className="mt-4 p-3 bg-gray-700 border border-gray-600 rounded-lg">
        <p className="text-sm text-gray-300">
          <span className="text-purple-400 font-semibold">💡 Tip:</span> Theme changes apply immediately. 
          The background animation will update throughout the app.
        </p>
      </div>
    </div>
  );
};

export default ThemeSelector;
